import {injectable, inject} from 'inversify';
import {ChannelProvider} from 'openfin/_v2/api/interappbus/channel/provider';
import {WindowOption} from 'openfin/_v2/api/window/windowOption';
import {_Window} from 'openfin/_v2/api/window/window';
import {ProviderIdentity} from 'openfin/_v2/api/interappbus/channel/channel';

import {OAUTH_CHANNEL, OAUTH_TOPIC, AUTH_IDENTITY, LAUNCHBAR_IDENTITY, DESKTOP_CHANNEL, DesktopChannelActions} from '../constants';
import {StartAuthentication, Authenticated, PostLogin, AccountInfo, Logout, LaunchAdminWindow, LaunchWindows} from '../store/partials/Authentication';
import {RootState} from '../store/State';
import {Action} from '../store/Store';
import {ServiceStore} from '../store/ServiceStore';
import {Inject} from '../common/Injectables';
import {Identity} from '../utils/platform';
import {adminWindows} from '../config/windows';
import {ROUTES} from '../components/Router/consts';
import {RefreshOrganization} from '../store/partials/Organization';
import {API} from '../model/API';
import {AuthData} from '../model/api/Auth';
import {UserData} from '../model/api/User';

import {AsyncInit} from './AsyncInit';

const isProduction = process.env.NODE_ENV === 'production';

const authWindowOptions: WindowOption = {
    name: AUTH_IDENTITY.name,
    alwaysOnTop: false,
    autoShow: false,
    opacity: 0,
    frame: true,
    contextMenu: !isProduction,
    defaultCentered: true,
    defaultHeight: 500,
    defaultWidth: 400,
    maximizable: false,
    minimizable: false,
    resizable: false,
    saveWindowState: false,
    shadow: true,
    showTaskbarIcon: true,
    url: ROUTES.OAUTH_LOGIN,
    waitForPageLoad: false
};

export interface AuthenticatedPayload {
    valid: boolean;
}

interface DesktopAuthenticatedPayload {
    endpoint: string;
    authenticated: boolean;
}

@injectable()
export class Authentication extends AsyncInit {
    // Channel for authenticating login
    private _authenticationChannel!: ChannelProvider;
    // Channel for communicating between OpenFin Desktop apps
    private _desktopProvider!: ChannelProvider;
    private _window?: _Window;

    private get loggedIn(): boolean {
        return this.store.state.authentication.loggedIn;
    }

    private get account(): AccountInfo {
        return this.store.state.authentication.account;
    }

    private get isOauth(): boolean {
        return this.store.state.authentication.environment.oauth;
    }

    private get isEnterprise(): boolean {
        return this.store.state.authentication.environment.enterprise;
    }

    private get isAdmin(): boolean {
        return (process.env.IS_ADMIN as string || 'false').toLowerCase() === 'true';
    }

    public constructor(
        @inject(Inject.STORE) private readonly store: ServiceStore,
        @inject(Inject.API) private readonly api: API
    ) {
        super();
        this.store.onAction.add(this.onAction, this);
    }

    protected async init() {
        await this.store.initialized;
        await this.registerAuthenticationChannel();
        await this.registerDesktopChannel();
    }

    private async registerDesktopChannel(): Promise<void> {
        this._desktopProvider = await fin.InterApplicationBus.Channel.create(DESKTOP_CHANNEL);
        this._desktopProvider.register(DesktopChannelActions.QUERY_AUTHENTICATION, this.getDesktopAuthenticatedPayload.bind(this));
    }

    private async registerAuthenticationChannel(): Promise<void> {
        this._authenticationChannel = await fin.InterApplicationBus.Channel.create(OAUTH_CHANNEL);
        this._authenticationChannel.register(OAUTH_TOPIC, async (payload: AuthenticatedPayload, id: ProviderIdentity) => {
            const identity: Identity = {name: id.name, uuid: id.uuid};
            console.log('Authentication message received', payload, identity);
            if (identity.name === AUTH_IDENTITY.name && identity.uuid !== AUTH_IDENTITY.uuid) {
                throw new Error('This channel should only be used by the post login window.');
            }
            const accountInfo = await this.getAccountInfo();
            if (accountInfo) {
                console.log(`Authentication successful - ${JSON.stringify(accountInfo)}`);
                await this.store.dispatch(new Authenticated(true, accountInfo));
                return true;
            } else {
                console.error('Account info invalid, logging out');
                await this.logout();
                return false;
            }
        });

        // If the login window has been closed and user is not logged in quit application
        const application = fin.Application.getCurrentSync();
        application.addListener('window-closed', (event) => {
            const {name} = event;
            if (name === authWindowOptions.name && !this.store.state.authentication.loggedIn) {
                application.quit();
            }
        });
    }

    private async onAction(action: Action<RootState>): Promise<void> {
        if (action instanceof StartAuthentication && !this.loggedIn) {
            if (!this.isEnterprise) {
                await this.store.dispatch(new Authenticated(true, {isAdmin: this.isAdmin}));
                return;
            }

            // Check to see if the session is still valid
            const accountInfo = await this.getAccountInfo();
            if (accountInfo) {
                console.log('Session token is still valid, skip login.');
                await this.store.dispatch(new Authenticated(true, accountInfo));
            } else {
                console.log('No session token, starting login.');
                await this.openAuthWindow('login');
            }
        } else if (action instanceof LaunchWindows) {
            await this.launchChildWindows();
        } else if (action instanceof Authenticated) {
            const {loggedIn} = action;

            this._desktopProvider.publish(DesktopChannelActions.AUTHENTICATED, this.getDesktopAuthenticatedPayload());
            if (loggedIn) {
                await this.postLogin();
            } else {
                await this.logout();
            }
        } else if (action instanceof Logout) {
            if (!this.isOauth) {
                await this.api.post(AuthData.LOGOUT, {});
            }
            await this.store.dispatch(new Authenticated(false));
        } else if (action instanceof LaunchAdminWindow) {
            await this.openAdminWindow();
        }
    }

    private getDesktopAuthenticatedPayload(): DesktopAuthenticatedPayload {
        return {
            endpoint: this.getNotificationEndpoint(),
            authenticated: this.store.state.authentication.loggedIn
        };
    }

    private getNotificationEndpoint(): string {
        let subDomain = window.location.hostname.replace('.openfin.co', '');

        if (subDomain === 'localhost') {
            subDomain = new URL(CONFIG.BACKEND).hostname.replace('.openfin.co', '');
        }

        let endpoint = '';
        if (subDomain.endsWith('-os-dev')) {
            endpoint = 'notes-dev';
        } else if (subDomain.endsWith('-os-staging')) {
            endpoint = 'notes-staging';
        } else {
            endpoint = 'notes.os';
        }

        endpoint = `https://${endpoint}.openfin.co`;
        console.log(`OFD endpoint: ${window.location.host} - Notes endpoint: ${endpoint}`);
        return endpoint;
    }

    private async openAuthWindow(type: 'login' | 'logout'): Promise<void> {
        // Whilst direct login is still being used
        let route = ROUTES.LOGIN;
        if (this.isOauth) {
            route = type === 'login' ? ROUTES.OAUTH_LOGIN : ROUTES.OAUTH_LOGOUT;
        }
        const frame = route !== ROUTES.LOGIN;
        if (!this._window) {
            // Can't use WebWindow as it on a different domain
            this._window = await fin.Window.create({...authWindowOptions, url: route, frame});
        } else {
            this._window.navigate(window.location.origin + route);
        }
        await this._window.focus();
        await this._window.bringToFront();
    }

    private async postLogin(): Promise<void> {
        console.log('Post authenticated login flow starting.');
        if (!this.loggedIn) {
            throw new Error('User is not logged in!');
        }
        console.log('Auth window state: ', this._window);
        if (this._window) {
            console.log('Closing authentication window.');
            await this._window.close(true);
            this._window = undefined;
        }

        try {
            await this.store.dispatch(new RefreshOrganization());
        } catch (e) {
            console.error('Error trying to refresh organization settings.', e);
        }

        console.log('Dispatching post login.');
        await this.store.dispatch(new PostLogin());
    }

    /**
     * Launch Admin windows and saga windows.
     */
    private async launchChildWindows(): Promise<void> {
        if (this.account.isAdmin) {
            // Open admin windows in the flow the user will be able to access them
            // This will help reduce load time
            await fin.Window.create(adminWindows.admin).catch(() => {
                fin.Window.wrapSync({name: adminWindows.admin.name, uuid: LAUNCHBAR_IDENTITY.uuid}).reload();
            });
        }
    }

    private async logout(): Promise<void> {
        console.log('Logging out.');
        await this.closeAllWindows();
        await this.openAuthWindow('logout');
        console.log('Successfully logged out.');
    }

    private async getAccountInfo(): Promise<AccountInfo | null> {
        if (!this.isEnterprise) {
            return {} as AccountInfo;
        }
        return this.api.get(UserData.INFO).catch(() => null);
    }

    private async closeAllWindows(): Promise<void> {
        const application = fin.Application.getCurrentSync();
        const childWindows = (await application.getChildWindows()).filter((win) => {
            return win.identity.name !== AUTH_IDENTITY.name || win.identity.name !== LAUNCHBAR_IDENTITY.name;
        });

        await Promise.all(childWindows.map(async (win) => {
            try {
                await fin.Window.wrapSync(win.identity).close();
            } catch (e) {
                console.error('Failed to close window:', win.identity, e);
            }
        }));
    }

    private async openAdminWindow(): Promise<void> {
        if (this.store.state.authentication.account.isAdmin) {
            const adminWindow = fin.Window.wrapSync({...LAUNCHBAR_IDENTITY, name: adminWindows.admin.id});
            await adminWindow.updateOptions({opacity: 1});
            await adminWindow.show();
            await adminWindow.focus();
            await adminWindow.bringToFront();
        } else {
            throw new Error('Unable to launch admin window, user is not an admin.');
        }
    }
}
